import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  ListItemText,
  ListSubheader,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import { 
  AccountCircle, 
  Login, 
  Logout, 
  BugReport, 
  LightMode, 
  DarkMode 
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme, useAppConfig, usePermissions, getActionTooltip } from '@/hooks';
import logo from '@/assets/LogistX.jpg';

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useAppConfig();
  const { mode, toggleTheme } = useTheme();
  const { can } = usePermissions();
  const { 
    isAuthenticated, 
    isLoading, 
    error, 
    account, 
    login, 
    logout 
  } = useAuth();
  const [bolAnchorEl, setBolAnchorEl] = useState<null | HTMLElement>(null);
  const [bolNotificationsAnchorEl, setBolNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingAuthAction, setPendingAuthAction] = useState<'login' | 'logout' | null>(null);

  const handleBolMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBolAnchorEl(event.currentTarget);
  };

  const handleBolMenuClose = () => {
    setBolAnchorEl(null);
  };

  const handleBolNotificationsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBolNotificationsAnchorEl(event.currentTarget);
  };

  const handleBolNotificationsMenuClose = () => {
    setBolNotificationsAnchorEl(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  const handleLoginClick = async () => {
    setPendingAuthAction('login');
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
      setPendingAuthAction(null);
    }
  };

  const handleLogout = async () => {
    setPendingAuthAction('logout');
    try {
      await logout();
      handleUserMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      setPendingAuthAction(null);
    }
  };

  const handleDebugClick = () => {
    handleUserMenuClose();
    navigate('/debug');
  };

  const isCurrentPage = (path: string) => location.pathname === path;

  React.useEffect(() => {
    if (!isLoading) {
      setPendingAuthAction(null);
    }
  }, [isLoading]);

  const authLoadingLabel = pendingAuthAction === 'logout' ? 'Signing out...' : 'Signing in...';

  // Permission checks for menu visibility
  const canAccessBOL = isAuthenticated && (can.readLogisticsObject || can.readMember || can.readHost);

  const canAccessBOLNotifications = isAuthenticated && can.manageSubscriptions;

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
            <img 
              src={logo} 
              alt="LogistX Logo" 
              style={{ 
                height: '32px', 
                width: 'auto', 
                marginRight: '12px',
                display: 'block',
                objectFit: 'contain'
              }} 
            />
            <Typography variant="h6" component="div">
              {config.appTitle || 'BOL Backoffice'}
            </Typography>
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
            
            {/* BOL Menu */}
            <Tooltip 
              title={!canAccessBOL ? 'Required role (any of): BOL.LOGISTICS-OBJECTS.Read, BOL.HOSTS.Read, BOL.MEMBERS.Read' : ''}
            >
              <span>
                <Button
                  color="inherit"
                  onClick={handleBolMenuOpen}
                  disabled={!canAccessBOL}
                  aria-controls="bol-menu"
                  aria-haspopup="true"
                >
                  BOL
                </Button>
              </span>
            </Tooltip>
            <Menu
              id="bol-menu"
              anchorEl={bolAnchorEl}
              open={Boolean(bolAnchorEl)}
              onClose={handleBolMenuClose}
              slotProps={{ list: { 'aria-labelledby': 'bol-menu' } }}
            >
              <ListSubheader>Logistics Objects</ListSubheader>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/activities"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/activities')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Activities</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/consignments"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/consignments')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Consignments</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/customer-orders"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/customer-orders')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Customer Orders</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/human-means"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/human-means')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Human Means</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/invoicing-documents"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/invoicing-documents')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Invoicing Documents</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/items"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/items')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Items</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/material-means"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/material-means')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Material Means</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/pieces"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/pieces')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Pieces</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/products"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/products')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Products</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/service-requests"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/service-requests')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Service Requests</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/quotation-requests"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/quotation-requests')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Quotation Requests</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/quotations"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/quotations')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Quotations</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readLogisticsObject ? getActionTooltip('readLogisticsObject') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/services"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/services')} 
                    disabled={!can.readLogisticsObject}
                  >
                    <ListItemText inset>Services</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Divider />
              <ListSubheader>Management</ListSubheader>
              <Tooltip 
                title={!can.readHost ? getActionTooltip('readHost') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/hosts"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/hosts')} 
                    disabled={!can.readHost}
                  >
                    <ListItemText inset>Hosts</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.readMember ? getActionTooltip('readMember') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/members"
                    onClick={handleBolMenuClose}
                    selected={isCurrentPage('/members')} 
                    disabled={!can.readMember}
                  >
                    <ListItemText inset>Members</ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>

            {/* BOL Notifications Menu */}
            <Tooltip 
              title={!canAccessBOLNotifications ? getActionTooltip('manageSubscriptions') : ''}
            >
              <span>
                <Button
                  color="inherit"
                  onClick={handleBolNotificationsMenuOpen}
                  disabled={!canAccessBOLNotifications}
                  aria-controls="bol-notifications-menu"
                  aria-haspopup="true"
                >
                  BOL Notifications
                </Button>
              </span>
            </Tooltip>
            <Menu
              id="bol-notifications-menu"
              anchorEl={bolNotificationsAnchorEl}
              open={Boolean(bolNotificationsAnchorEl)}
              onClose={handleBolNotificationsMenuClose}
              slotProps={{ list: { 'aria-labelledby': 'bol-notifications-menu' } }}
            >
              <Tooltip 
                title={!can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/subscriptions"
                    onClick={handleBolNotificationsMenuClose}
                    selected={isCurrentPage('/subscriptions')} 
                    disabled={!can.manageSubscriptions}
                  >
                    Subscriptions
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip 
                title={!can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : ''} 
                placement="right"
              >
                <span>
                  <MenuItem 
                    component={Link}
                    to="/consumers"
                    onClick={handleBolNotificationsMenuClose}
                    selected={isCurrentPage('/consumers')} 
                    disabled={!can.manageSubscriptions}
                  >
                    Consumers
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>

          </Box>
          {/* Show authentication status */}
          {isAuthenticated ? (
            <>
              {account && (
                <Chip 
                  label={account.username || account.name || 'User'} 
                  color="secondary" 
                  size="small" 
                  sx={{ mr: 1 }} 
                />
              )}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
                disabled={isLoading}
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={userAnchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(userAnchorEl)}
                onClose={handleUserMenuClose}
                slotProps={{ list: { autoFocusItem: false } }}
              >
                <MenuItem onClick={handleDebugClick} disabled={isLoading}>
                  <ListItemIcon>
                    <BugReport fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Debug Token</ListItemText>
                </MenuItem>
                <MenuItem onClick={toggleTheme}>
                  <ListItemIcon>
                    {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>
                    Switch to {mode === 'dark' ? 'Light' : 'Dark'} Theme
                  </ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} disabled={isLoading}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button 
              color="inherit" 
              onClick={handleLoginClick}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Login />}
            >
              {isLoading ? authLoadingLabel : 'Sign in'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Show error message if authentication fails */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          Authentication error: {error}
        </Alert>
      )}
    </>
  );
};

export default AppHeader;
